/**
 * Mautic Form Embed Component
 *
 * Dynamically loads and embeds Mautic forms via their JavaScript embed.
 * Supports custom styling through className prop.
 */

import React, { useEffect, useRef } from 'react';

interface MauticFormProps {
  formId: number;
  className?: string;
  onLoad?: () => void;
}

const MAUTIC_BASE_URL = 'https://mautic.cloudgeeks.com.au';

export const MauticForm: React.FC<MauticFormProps> = ({
  formId,
  className = '',
  onLoad,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current || scriptLoadedRef.current) return;

    // Create and load the Mautic form script
    const script = document.createElement('script');
    script.src = `${MAUTIC_BASE_URL}/form/generate.js?id=${formId}`;
    script.async = true;
    script.onload = () => {
      scriptLoadedRef.current = true;
      onLoad?.();
    };

    // Append script to container
    containerRef.current.appendChild(script);

    // Cleanup on unmount
    return () => {
      if (containerRef.current) {
        const existingScript = containerRef.current.querySelector('script');
        if (existingScript) {
          containerRef.current.removeChild(existingScript);
        }
      }
      scriptLoadedRef.current = false;
    };
  }, [formId, onLoad]);

  return (
    <div
      ref={containerRef}
      className={`mautic-form-container ${className}`}
      data-mautic-form-id={formId}
    />
  );
};

export default MauticForm;
